import { useMemo } from 'react';
import { Button, Empty, Tooltip } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { MatrixSeverityType, MatrixLikelihoodType, MatrixRiskType } from '../../../types/types';
import { useTranslation } from 'react-i18next';

type CellPayload = {
  severity: MatrixSeverityType;
  likelihood: MatrixLikelihoodType;
  risk?: MatrixRiskType;
};

type RiskMatrixProps = {
  severityLevels: MatrixSeverityType[];
  likelihoodLevels: MatrixLikelihoodType[];
  riskLevels: MatrixRiskType[];
  inverse?: boolean;
  onSelectCell?: (payload: CellPayload) => void;
  onAddSeverity?: () => void;
  onAddLikelihood?: () => void;
  onEditSeverity?: (severity: MatrixSeverityType) => void;
  onEditLikelihood?: (likelihood: MatrixLikelihoodType) => void;
  onDeleteSeverity?: (severity: MatrixSeverityType) => void;
  onDeleteLikelihood?: (likelihood: MatrixLikelihoodType) => void;
};

const buildKey = (severity: MatrixSeverityType, likelihood: MatrixLikelihoodType) =>
  `${severity.id ?? severity.name}-${likelihood.id ?? likelihood.name}`;

const RiskMatrix: React.FC<RiskMatrixProps> = ({
  severityLevels,
  likelihoodLevels,
  riskLevels,
  inverse = false,
  onSelectCell,
  onAddSeverity,
  onAddLikelihood,
  onEditSeverity,
  onEditLikelihood,
  onDeleteSeverity,
  onDeleteLikelihood,
}) => {
  const { t } = useTranslation();
  const riskMap = useMemo(() => {
    const map = new Map<string, MatrixRiskType>();
    riskLevels.forEach((risk) => {
      if (risk.severity && risk.likelihood) {
        map.set(buildKey(risk.severity, risk.likelihood), risk);
      }
    });
    return map;
  }, [riskLevels]);

  const rowHeaders = inverse ? severityLevels : likelihoodLevels;
  const columnHeaders = inverse ? likelihoodLevels : severityLevels;

  const severityHeaderStyle = {
    height: '40px',
    border: '1px solid #ccc',
    background: '#85b7e6ff',
    color: '#385079ff',
    cursor: onEditSeverity ? 'pointer' : 'default',
  };

  const likelihoodHeaderStyle = {
    height: '40px',
    border: '1px solid #ccc',
    background: '#bcc599ff',
    color: '#4a5d23ff',
    cursor: onEditLikelihood ? 'pointer' : 'default',
  };

  const columnWidth = columnHeaders.length ? 100 / (columnHeaders.length + 1) : 0;
  const rowHeight = rowHeaders.length ? 100 / (rowHeaders.length + 1) : 0;
  const addSeverityLabel = inverse ? t('Agregar fila (Severidad)') : t('Agregar columna (Severidad)');
  const addLikelihoodLabel = inverse ? t('Agregar columna (Probabilidad)') : t('Agregar fila (Probabilidad)');

  const handleCellClick = (payload: CellPayload) => {
    if (!onSelectCell) return;
    onSelectCell(payload);
  };

  const renderTable = () => {
    if (!severityLevels.length || !likelihoodLevels.length) {
      return <Empty description={t('Agrega columnas y filas para comenzar')} />;
    }

    return (
      <table style={{ width: '100%', minHeight: '70vh' }}>
        <thead>
          <tr>
            <th style={{ width: `${columnWidth}%` }}></th>
            {columnHeaders.map((columnItem) => (
              <th
                key={columnItem.id ?? columnItem.name}
                style={{
                  width: `${columnWidth}%`,
                  ...(inverse ? likelihoodHeaderStyle : severityHeaderStyle),
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                  onClick={() =>
                    inverse
                      ? onEditLikelihood && onEditLikelihood(columnItem as MatrixLikelihoodType)
                      : onEditSeverity && onEditSeverity(columnItem as MatrixSeverityType)
                  }
                >
                  <span>{columnItem.name}</span>
                  {(!inverse && onDeleteSeverity) || (inverse && onDeleteLikelihood) ? (
                    <Tooltip title={t('Eliminar columna')}>
                      <Button
                        size='small'
                        type='text'
                        style={{ color: '#003270' }}
                        icon={<DeleteOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          inverse
                            ? onDeleteLikelihood && onDeleteLikelihood(columnItem as MatrixLikelihoodType)
                            : onDeleteSeverity && onDeleteSeverity(columnItem as MatrixSeverityType);
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowHeaders.map((rowItem) => (
            <tr key={rowItem.id ?? rowItem.name}>
              <td
                style={{
                  height: `${rowHeight}%`,
                  border: '1px solid #ccc',
                  textAlign: 'center',
                  backgroundColor: inverse ? '#85b7e6ff' : '#bcc599ff',
                  color: inverse ? '#385079ff' : '#4a5d23ff',
                  fontWeight: 600,
                  cursor: inverse ? (onEditSeverity ? 'pointer' : 'default') : onEditLikelihood ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
                  onClick={() =>
                    inverse
                      ? onEditSeverity && onEditSeverity(rowItem as MatrixSeverityType)
                      : onEditLikelihood && onEditLikelihood(rowItem as MatrixLikelihoodType)
                  }
                >
                  <span>{rowItem.name}</span>
                  {(inverse ? onDeleteSeverity : onDeleteLikelihood) && (
                    <Tooltip title={t('Eliminar fila')}>
                      <Button
                        size='small'
                        type='text'
                        style={{ color: '#003270' }}
                        icon={<DeleteOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          inverse
                            ? onDeleteSeverity && onDeleteSeverity(rowItem as MatrixSeverityType)
                            : onDeleteLikelihood && onDeleteLikelihood(rowItem as MatrixLikelihoodType);
                        }}
                      />
                    </Tooltip>
                  )}
                </div>
              </td>
              {columnHeaders.map((columnItem) => {
                const severity = inverse ? (rowItem as MatrixSeverityType) : (columnItem as MatrixSeverityType);
                const likelihood = inverse ? (columnItem as MatrixLikelihoodType) : (rowItem as MatrixLikelihoodType);
                const risk = riskMap.get(buildKey(severity, likelihood));
                return (
                  <td
                    key={`${likelihood.id ?? likelihood.name}-${severity.id ?? severity.name}`}
                    onClick={() => handleCellClick({ severity, likelihood, risk })}
                    style={{
                      cursor: onSelectCell ? 'pointer' : 'default',
                      backgroundColor: `#${risk?.cell_color ?? 'ffffff'}`,
                      color: `#${risk?.font_color ?? '000000'}`,
                      height: `${rowHeight}%`,
                      textAlign: 'center',
                      border: '1px solid #ccc',
                      padding: '0.5rem',
                      verticalAlign: 'middle',
                    }}
                  >
                    {risk?.name ?? t('Editar')}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className='risk-matrix-wrapper'>
      <div
        className='matrix-action-bar'
        style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '1rem' }}
      >
        <Button icon={<PlusCircleOutlined />} onClick={onAddSeverity} type='dashed' disabled={!onAddSeverity}>
          {addSeverityLabel}
        </Button>
        <Button icon={<PlusCircleOutlined />} onClick={onAddLikelihood} type='dashed' disabled={!onAddLikelihood}>
          {addLikelihoodLabel}
        </Button>
      </div>
      {renderTable()}
    </div>
  );
};

export default RiskMatrix;
